import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from '@almothafar/angular-signature-pad';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Question } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';

@Component({
  selector: 'app-question-signature',
  templateUrl: './question-signature.component.html',
  styleUrls: ['./question-signature.component.scss'],
})
export class QuestionSignatureComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('question') question!: Question;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  @ViewChild('signature', { static: false })
  public signaturePad!: SignaturePadComponent;

  @ViewChild('signatureContainer', { static: false })
  private signatureContainer!: ElementRef<HTMLDivElement>;

  signaturePadOptions: NgSignaturePadOptions = {
    canvasHeight: 300,
    canvasWidth: 500,
    minWidth: 2,
    maxWidth: 3,
  };

  hasSignature = false;
  inkGuardError: string | null = null;

  // Threshold from the CPK-4349 signature forensics study (18/08/2026), same
  // guard already enforced for contract signing (CPK-4368): strokes narrower
  // than this are blank canvases or single-tap artifacts, not signatures.
  private static readonly MIN_STROKE_WIDTH_PX = 15;

  private resizeObserver?: ResizeObserver;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    this.question.isValid = true;

    if (this.question.answer?.addtional_information) {
      this.hasSignature = true;
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && this.signaturePad) {
          const currentData = (!this.signaturePad.isEmpty()
            ? this.signaturePad.toDataURL()
            : (this.question.answer?.addtional_information as string)) || null;

          this.signaturePad.set('canvasWidth', width);
          this.signaturePad.set('canvasHeight', height);

          if (currentData) {
            this.signaturePad.fromDataURL(currentData, { width, height });
          }
        }
      }
    });

    if (this.signatureContainer?.nativeElement) {
      this.resizeObserver.observe(this.signatureContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  drawComplete(event: MouseEvent | Touch) {
    this.saveSignature();
  }

  drawStart(event: MouseEvent | Touch) {
    this.hasSignature = true;
  }

  clearSignature() {
    this.signaturePad.clear();
    this.hasSignature = false;
    this.question.answer = {
      value: null,
      addtional_information: null,
      isIgnored: false,
    };
    // A cleared canvas has no ink — same as never having signed. A required
    // signature must not be marked valid, or "Prosseguir" stays enabled with a
    // blank signature. An optional one (CPK-4894) is valid blank, as if never
    // touched: `elective` here is already the effective value (setQuestionMap()
    // coerces Simple Questionnaire skippable questions to true).
    this.question.isValid = this.question.elective === true;
    this.question.messageError = undefined;
    this.inkGuardError = null;
    this.change();
  }

  saveSignature() {
    const result = this.checkInkBounds(this.signaturePad);

    if (!result.valid) {
      this.question.answer = {
        value: null,
        addtional_information: null,
        isIgnored: false,
      };
      this.question.isValid = false;
      this.question.messageError =
        result.reason === 'no_ink' ? 'SignatureBlankTryAgain' : 'SignatureTooSmallTryAgain';
    } else {
      const base64 = this.signaturePad.toDataURL();
      this.question.answer = {
        value: 'SIGNED',
        addtional_information: base64,
        isIgnored: false,
      };
      this.question.isValid = true;
      this.question.messageError = undefined;
    }

    this.inkGuardError = this.question.isValid ? null : this.question.messageError ?? null;
    this.change();
  }

  /**
   * Same criteria as signatureInkGuard.ts (CPK-4368): no ink at all, or a
   * stroke narrower than MIN_STROKE_WIDTH_PX (blank canvas / single-tap
   * artifact), is rejected — matches the CPK-4349 forensics threshold.
   */
  private checkInkBounds(pad: SignaturePadComponent): {
    valid: boolean;
    reason?: 'no_ink' | 'stroke_too_narrow';
  } {
    const points = pad.toData();

    if (!points || points.length === 0) {
      return { valid: false, reason: 'no_ink' };
    }

    let minX = Infinity;
    let maxX = -Infinity;

    for (const group of points) {
      for (const point of (group as any).points ?? group) {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
      }
    }

    if (!isFinite(minX)) {
      return { valid: false, reason: 'no_ink' };
    }

    if (maxX - minX < QuestionSignatureComponent.MIN_STROKE_WIDTH_PX) {
      return { valid: false, reason: 'stroke_too_narrow' };
    }

    return { valid: true };
  }

  change() {
    setTimeout(() => {
      // isValid is already set by saveSignature()/clearSignature() before this
      // runs — forcing it to true here (as this used to do) silently discarded
      // the ink guard result by the time the parent page read it.
      this.changeAnswer.next({
        answer: this.question.answer!,
        question: this.question,
      });
    }, 10);
  }
}
